import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  PlusIcon,
  CalendarIcon,
  UserIcon,
  DocumentTextIcon,
  FunnelIcon
} from '@heroicons/react/24/outline';
import Card from '../components/common/Card';
import TimeInput12h from '../components/common/TimeInput12h';
import Button from '../components/common/Button';
import Modal from '../components/ui/Modal';
import Dropdown from '../components/ui/Dropdown';
import { useAuth } from '../contexts/AuthContext';
import { useHeaderSearch } from '../contexts/SearchContext';
import { useForm } from '../hooks/useForm';
import { useAsyncAction } from '../hooks/useApi';
import { AttendanceService } from '../services/attendance.service';
import { 
  attendanceRectificationSchema, 
  TimeValidation,
  defaultAttendanceData 
} from '../schemas/attendance.schema';
import { formatTime12hFromDb } from '../utils/timeDisplay';
import toast from '../utils/toast';
import { formatDateDisplay } from '../utils/dateDisplay';

// Helper Components
const LoadingSkeleton = () => (
  <div className="space-y-4">
    {[...Array(3)].map((_, i) => (
      <div key={i} className="animate-pulse">
        <Card>
          <Card.Body className="p-6">
            <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="h-16 bg-gray-200 rounded"></div>
              <div className="h-16 bg-gray-200 rounded"></div>
              <div className="h-16 bg-gray-200 rounded"></div>
            </div>
            <div className="h-20 bg-gray-200 rounded"></div>
          </Card.Body>
        </Card>
      </div>
    ))}
  </div>
);

const EmptyState = ({ icon: Icon, title, description, action }) => (
  <Card>
    <Card.Body className="text-center py-12">
      <Icon className="mx-auto h-16 w-16 text-gray-400 mb-4" />
      <h3 className="text-lg font-medium text-gray-900 mb-2">{title}</h3>
      <p className="text-gray-600 mb-6">{description}</p>
      {action}
    </Card.Body>
  </Card>
);

const RequestCard = ({ request, index }) => {
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return formatDateDisplay(dateString);
  };

  const getStatusBadge = (status, pendingStage) => {
    if (status === 'accepted') {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <CheckCircleIcon className="w-3 h-3 mr-1" />
          Approved
        </span>
      );
    }
    if (status === 'rejected') {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <XCircleIcon className="w-3 h-3 mr-1" />
          Rejected
        </span>
      );
    }
    const stage = pendingStage === 'hr' ? 'hr' : 'team_lead';
    return (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
          stage === 'hr' ? 'bg-blue-100 text-blue-800' : 'bg-yellow-100 text-yellow-800'
        }`}
      >
        <ExclamationTriangleIcon className="w-3 h-3 mr-1" />
        {stage === 'hr' ? 'Pending HR' : 'Pending Team Lead'}
      </span>
    );
  };

  const getDateLabel = () => {
    if (request.totalDays && request.totalDays > 1 && request.startDate && request.endDate) {
      return `${formatDate(request.startDate)} - ${formatDate(request.endDate)}`;
    }
    return formatDate(request.datestring || request.startDate);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
    >
      <Card className="hover:shadow-lg transition-shadow duration-300">
        <Card.Body className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center">
                <CalendarIcon className="w-6 h-6 text-indigo-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {getDateLabel()}
                </h3>
                <p className="text-sm text-gray-600">Request #{request.id}</p>
                {request.totalDays > 1 && (
                  <p className="text-xs text-indigo-600">{request.totalDays} days combined</p>
                )}
              </div>
            </div>
            <div className="text-right">
              {getStatusBadge(request.request, request.pendingStage)}
            </div>
          </div>

          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center space-x-2">
              <ClockIcon className="w-5 h-5 text-gray-400" />
              <div>
                <p className="text-sm text-gray-600">In Time</p>
                <p className="font-medium">{formatTime12hFromDb(request.intime)}</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <ClockIcon className="w-5 h-5 text-gray-400" />
              <div>
                <p className="text-sm text-gray-600">Out Time</p>
                <p className="font-medium">{formatTime12hFromDb(request.outtime)}</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <ClockIcon className="w-5 h-5 text-gray-400" />
              <div>
                <p className="text-sm text-gray-600">Work Hours</p>
                <p className="font-medium">{request.worktime || 'N/A'}</p>
              </div>
            </div>
          </div>

          {request.reason && (
            <div className="mt-4 flex items-start space-x-2">
              <DocumentTextIcon className="w-5 h-5 text-gray-400 mt-0.5" />
              <div>
                <p className="text-sm text-gray-600">Reason</p>
                <p className="text-gray-900">{request.reason}</p>
              </div>
            </div>
          )}
        </Card.Body>
      </Card>
    </motion.div>
  );
};

const ApprovedApprovalCard = ({ request, index }) => {
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return formatDateDisplay(dateString);
  };

  const getDateLabel = () => {
    if (request.totalDays && request.totalDays > 1 && request.startDate && request.endDate) {
      return `${formatDate(request.startDate)} - ${formatDate(request.endDate)}`;
    }
    return formatDate(request.datestring || request.startDate);
  };

  const formatApprovedAt = (dateString) => {
    if (!dateString) return null;
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return 'N/A';
    return `${formatDateDisplay(date)} ${date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`;
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
    >
      <Card className="hover:shadow-lg transition-shadow duration-300 border border-green-100">
        <Card.Body className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                <UserIcon className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {request.name || 'Employee Name'}
                </h3>
                <p className="text-sm text-gray-600">{request.email_address}</p>
              </div>
            </div>
            <div className="text-right">
              <span className="inline-flex items-center px-3 py-1 text-sm font-medium rounded-full bg-green-100 text-green-800">
                <CheckCircleIcon className="w-4 h-4 mr-1" />
                Approved
              </span>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center space-x-2">
              <CalendarIcon className="w-5 h-5 text-gray-400" />
              <div>
                <p className="text-sm text-gray-600">Date</p>
                <p className="font-medium">{getDateLabel()}</p>
                {request.totalDays > 1 && (
                  <p className="text-xs text-indigo-600">{request.totalDays} days combined</p>
                )}
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <ClockIcon className="w-5 h-5 text-gray-400" />
              <div>
                <p className="text-sm text-gray-600">In Time</p>
                <p className="font-medium">{formatTime12hFromDb(request.intime)}</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <ClockIcon className="w-5 h-5 text-gray-400" />
              <div>
                <p className="text-sm text-gray-600">Out Time</p>
                <p className="font-medium">{formatTime12hFromDb(request.outtime)}</p>
              </div>
            </div>
          </div>

          {request.reason && (
            <div className="mt-4 flex items-start space-x-2">
              <DocumentTextIcon className="w-5 h-5 text-gray-400 mt-0.5" />
              <div>
                <p className="text-sm text-gray-600">Reason</p>
                <p className="text-gray-900">{request.reason}</p>
              </div>
            </div>
          )}

          {request.approvedAt && (
            <p className="mt-4 text-xs text-gray-500">
              Approved on {formatApprovedAt(request.approvedAt)}
              {request.approvedBy ? ` by ${request.approvedBy}` : ''}
            </p>
          )}
        </Card.Body>
      </Card>
    </motion.div>
  );
};

const ApprovalCard = ({ request, index, onApprove, onReject, approving }) => {
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return formatDateDisplay(dateString);
  };

  const getDateLabel = () => {
    if (request.totalDays && request.totalDays > 1 && request.startDate && request.endDate) {
      return `${formatDate(request.startDate)} - ${formatDate(request.endDate)}`;
    }
    return formatDate(request.datestring || request.startDate);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1 }}
    >
      <Card className="hover:shadow-lg transition-shadow duration-300">
        <Card.Body className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                <UserIcon className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  {request.name || 'Employee Name'}
                </h3>
                <p className="text-sm text-gray-600">{request.email_address}</p>
              </div>
            </div>
            <div className="text-right">
              <span className={`px-3 py-1 text-sm font-medium rounded-full ${
                request.pendingStage === 'hr' ? 'bg-blue-100 text-blue-800' : 'bg-yellow-100 text-yellow-800'
              }`}>
                {request.pendingStage === 'hr' ? 'Pending HR' : 'Pending Team Lead'}
              </span>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center space-x-2">
              <CalendarIcon className="w-5 h-5 text-gray-400" />
              <div>
                <p className="text-sm text-gray-600">Date</p>
                <p className="font-medium">{getDateLabel()}</p>
                {request.totalDays > 1 && (
                  <p className="text-xs text-indigo-600">{request.totalDays} days combined</p>
                )}
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <ClockIcon className="w-5 h-5 text-gray-400" />
              <div>
                <p className="text-sm text-gray-600">In Time</p>
                <p className="font-medium">{formatTime12hFromDb(request.intime)}</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <ClockIcon className="w-5 h-5 text-gray-400" />
              <div>
                <p className="text-sm text-gray-600">Out Time</p>
                <p className="font-medium">{formatTime12hFromDb(request.outtime)}</p>
              </div>
            </div>
          </div>

          {request.reason && (
            <div className="mt-4 flex items-start space-x-2">
              <DocumentTextIcon className="w-5 h-5 text-gray-400 mt-0.5" />
              <div>
                <p className="text-sm text-gray-600">Reason</p>
                <p className="text-gray-900">{request.reason}</p>
              </div>
            </div>
          )}

          <div className="mt-6 flex space-x-3">
            <Button
              variant="success"
              onClick={() => onApprove(request.id)}
              className="flex items-center space-x-2"
              loading={approving}
              disabled={approving}
            >
              <CheckCircleIcon className="w-4 h-4" />
              <span>{request.pendingStage === 'team_lead' ? 'Forward to HR' : 'Approve'}</span>
            </Button>
            <Button
              variant="danger"
              onClick={() => onReject(request)}
              className="flex items-center space-x-2"
              disabled={approving}
            >
              <XCircleIcon className="w-4 h-4" />
              <span>Reject</span>
            </Button>
          </div>
        </Card.Body>
      </Card>
    </motion.div>
  );
};

/** Normalize API date values (ISO strings, Date) to YYYY-MM-DD in local time for filter inputs. */
function toLocalDateKey(value) {
  if (value == null || value === '') return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

const AttendanceCorrection = () => {
  const { user, isAdmin, isHR, isTeamLead } = useAuth();
  const { headerSearch } = useHeaderSearch();
  const location = useLocation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('requests');
  const [showRequestForm, setShowRequestForm] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');

  // Filter states
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: ''
  });

  // State for data
  const [myRequests, setMyRequests] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [approvedApprovals, setApprovedApprovals] = useState([]);
  const [myRequestsLoading, setMyRequestsLoading] = useState(false);
  const [approvalsLoading, setApprovalsLoading] = useState(false);
  const [approvedLoading, setApprovedLoading] = useState(false);
  const [myRequestsPage, setMyRequestsPage] = useState(1);
  const [approvalsPage, setApprovalsPage] = useState(1);
  const [approvedPage, setApprovedPage] = useState(1);
  const [myRequestsHasMore, setMyRequestsHasMore] = useState(false);
  const [approvalsHasMore, setApprovalsHasMore] = useState(false);
  const [approvedHasMore, setApprovedHasMore] = useState(false);
  const [myRequestsLoadingMore, setMyRequestsLoadingMore] = useState(false);
  const [approvalsLoadingMore, setApprovalsLoadingMore] = useState(false);
  const [approvedLoadingMore, setApprovedLoadingMore] = useState(false);
  const requestFormRef = useRef(null);

  // Form management with validation
  const {
    values: formData,
    errors,
    handleChange,
    handleSubmit,
    reset: resetForm,
    isValid
  } = useForm(defaultAttendanceData, attendanceRectificationSchema, {
    validateOnChange: true,
    resetOnSubmit: true
  });

  // Async actions
  const { execute: submitRequest, loading: submitting } = useAsyncAction(
    AttendanceService.createRectificationRequest,
    {
      successMessage: 'Attendance correction request submitted successfully',
      errorMessage: 'Failed to submit attendance correction request'
    }
  );

  const { execute: approveRequest, loading: approving } = useAsyncAction(
    (id) => AttendanceService.updateRectificationRequest(id, { request: 'accepted' }),
    {
      successMessage: 'Request approved successfully',
      errorMessage: 'Failed to approve request'
    }
  );

  const { execute: rejectRequest } = useAsyncAction(
    (id, reason) => AttendanceService.updateRectificationRequest(id, { 
      request: 'rejected', 
      reason 
    }),
    {
      successMessage: 'Request rejected successfully',
      errorMessage: 'Failed to reject request'
    }
  );

  useEffect(() => {
    if (activeTab === 'requests') {
      fetchMyRequests(1, false);
    } else if (activeTab === 'approvals' && (isAdmin || isHR || isTeamLead)) {
      fetchPendingApprovals(1, false);
      fetchApprovedApprovals(1, false);
    }
  }, [activeTab, isAdmin, isHR, isTeamLead]);

  useEffect(() => {
    if (!showRequestForm || !requestFormRef.current) return;
    const id = window.setTimeout(() => {
      requestFormRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }, 80);
    return () => window.clearTimeout(id);
  }, [showRequestForm]);

  useEffect(() => {
    if (!location.state?.newRequest) return;

    setActiveTab('requests');
    setShowRequestForm(true);

    navigate(location.pathname, { replace: true, state: {} });
  }, [location.pathname, location.state, navigate]);

  const mergeRequestsById = (existing = [], incoming = []) => {
    const requestMap = new Map(existing.map((item) => [item.id, item]));
    incoming.forEach((item) => requestMap.set(item.id, item));
    return Array.from(requestMap.values());
  };

  const fetchMyRequests = async (page = 1, append = false) => {
    try {
      if (append) {
        setMyRequestsLoadingMore(true);
      } else {
        setMyRequestsLoading(true);
      }

      const response = await AttendanceService.getRectificationRequests(page);
      const records = response.records || [];
      setMyRequests((prev) => (append ? mergeRequestsById(prev, records) : records));
      setMyRequestsPage(page);
      setMyRequestsHasMore(Boolean(response.hasMore));
    } catch (error) {
      console.error('Error fetching my requests:', error);
      toast.error('Failed to load requests');
      if (!append) {
        setMyRequests([]);
        setMyRequestsPage(1);
        setMyRequestsHasMore(false);
      }
    } finally {
      if (append) {
        setMyRequestsLoadingMore(false);
      } else {
        setMyRequestsLoading(false);
      }
    }
  };

  const fetchPendingApprovals = async (page = 1, append = false) => {
    try {
      if (append) {
        setApprovalsLoadingMore(true);
      } else {
        setApprovalsLoading(true);
      }

      const response = await AttendanceService.getRectificationRequestsByManager(page, 'pending');
      const records = response.records || [];
      setPendingApprovals((prev) => (append ? mergeRequestsById(prev, records) : records));
      setApprovalsPage(page);
      setApprovalsHasMore(Boolean(response.hasMore));
    } catch (error) {
      console.error('Error fetching pending approvals:', error);
      toast.error('Failed to load pending approvals');
      if (!append) {
        setPendingApprovals([]);
        setApprovalsPage(1);
        setApprovalsHasMore(false);
      }
    } finally {
      if (append) {
        setApprovalsLoadingMore(false);
      } else {
        setApprovalsLoading(false);
      }
    }
  };

  const fetchApprovedApprovals = async (page = 1, append = false) => {
    try {
      if (append) {
        setApprovedLoadingMore(true);
      } else {
        setApprovedLoading(true);
      }

      const response = await AttendanceService.getRectificationRequestsByManager(page, 'accepted');
      const records = response.records || [];
      setApprovedApprovals((prev) => (append ? mergeRequestsById(prev, records) : records));
      setApprovedPage(page);
      setApprovedHasMore(Boolean(response.hasMore));
    } catch (error) {
      console.error('Error fetching approved requests:', error);
      toast.error('Failed to load approved requests');
      if (!append) {
        setApprovedApprovals([]);
        setApprovedPage(1);
        setApprovedHasMore(false);
      }
    } finally {
      if (append) {
        setApprovedLoadingMore(false);
      } else {
        setApprovedLoading(false);
      }
    }
  };

  const refreshApprovalsInbox = () => {
    fetchPendingApprovals(1, false);
    fetchApprovedApprovals(1, false);
  };

  const handleSubmitRequest = handleSubmit(async (values) => {
    // Validate time logic
    if (!TimeValidation.isOutTimeAfterInTime(values.intime, values.outtime)) {
      toast.error('Out time must be after in time');
      return;
    }

    const startDate = new Date(values.startDate);
    const endDate = new Date(values.endDate);
    const rangeDays = Math.floor((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;

    if (endDate < startDate) {
      toast.error('End date must be on or after start date');
      return;
    }

    if (rangeDays > 10) {
      toast.error('You can apply attendance correction for a maximum of 10 days');
      return;
    }

    // Disallow requests that include weekend dates.
    const currentDate = new Date(startDate);
    while (currentDate <= endDate) {
      const day = currentDate.getDay();
      if (day === 0 || day === 6) {
        toast.error('Requests for Saturday and Sunday are not allowed');
        return;
      }
      currentDate.setDate(currentDate.getDate() + 1);
    }

    const requestData = {
      ...values,
      name: user?.first_name + ' ' + user?.last_name,
      email_address: user?.email_address || user?.email
    };

    await submitRequest(requestData);
    setShowRequestForm(false);
    resetForm();
    
    if (activeTab === 'requests') {
      fetchMyRequests(1, false);
    }
  });

  const handleApproveRequest = async (requestId) => {
    await approveRequest(requestId);
    refreshApprovalsInbox();
  };

  const handleRejectRequest = async () => {
    if (!rejectionReason.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }

    await rejectRequest(selectedRequest.id, rejectionReason);
    setShowRejectModal(false);
    setRejectionReason('');
    setSelectedRequest(null);
    refreshApprovalsInbox();
  };

  const handleLoadMoreRequests = async () => {
    if (!myRequestsHasMore || myRequestsLoadingMore) return;
    await fetchMyRequests(myRequestsPage + 1, true);
  };

  const handleLoadMoreApprovals = async () => {
    if (!approvalsHasMore || approvalsLoadingMore) return;
    await fetchPendingApprovals(approvalsPage + 1, true);
  };

  const handleLoadMoreApproved = async () => {
    if (!approvedHasMore || approvedLoadingMore) return;
    await fetchApprovedApprovals(approvedPage + 1, true);
  };

  // Filter functions
  const filterRequests = (requests) => {
    return requests.filter(request => {
      // Status filter
      if (statusFilter !== 'all' && request.request !== statusFilter) {
        return false;
      }

      // Search filter (combine local and header search)
      const combinedSearch = headerSearch;
      if (combinedSearch) {
        const searchLower = combinedSearch.toLowerCase();
        const matchesSearch =
          (request.name && request.name.toLowerCase().includes(searchLower)) ||
          (request.reason && request.reason.toLowerCase().includes(searchLower)) ||
          (request.email_address && request.email_address.toLowerCase().includes(searchLower));

        if (!matchesSearch) return false;
      }

      // Date range filter (compare calendar days; API uses ISO datetimes, inputs are YYYY-MM-DD)
      const requestDateKey = toLocalDateKey(request.datestring);
      if (dateRange.startDate) {
        if (!requestDateKey || requestDateKey < dateRange.startDate) return false;
      }
      if (dateRange.endDate) {
        if (!requestDateKey || requestDateKey > dateRange.endDate) return false;
      }

      return true;
    });
  };

  const clearFilters = () => {
    setStatusFilter('all');
    setDateRange({ startDate: '', endDate: '' });
  };

  // Status filter options
  const statusOptions = [
    { value: 'all', label: 'All Status', icon: DocumentTextIcon },
    { value: 'pending', label: 'Pending', icon: ClockIcon },
    { value: 'accepted', label: 'Approved', icon: CheckCircleIcon },
    { value: 'rejected', label: 'Rejected', icon: XCircleIcon }
  ];

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return formatDateDisplay(dateString);
  };

  const showPendingApprovalsSection =
    activeTab !== 'approvals' || statusFilter === 'all' || statusFilter === 'pending';
  const showApprovedApprovalsSection =
    activeTab !== 'approvals' || statusFilter === 'all' || statusFilter === 'accepted';

  const loading = myRequestsLoading || approvalsLoading || approvedLoading;

  if (loading && myRequests.length === 0 && pendingApprovals.length === 0 && approvedApprovals.length === 0) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-32 bg-gray-200 rounded-xl mb-6"></div>
          <div className="space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-24 bg-gray-200 rounded-xl"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl p-6 text-white"
      >
        <h1 className="text-3xl font-bold mb-2">Attendance Correction</h1>
        <p className="text-indigo-100">Request corrections for attendance records or review and approve team requests</p>
      </motion.div>

      {/* Filter Tabs */}
      <Card>
        <Card.Body className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex space-x-4">
              <Button
                variant={activeTab === 'requests' ? 'primary' : 'outline'}
                onClick={() => setActiveTab('requests')}
                className="flex items-center space-x-2"
              >
                <DocumentTextIcon className="w-4 h-4" />
                <span>My Requests</span>
                {myRequests && myRequests.length > 0 && (
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    activeTab === 'requests' ? 'bg-white text-indigo-600' : 'bg-gray-100 text-gray-600'
                  }`}>
                    {myRequests.length}
                  </span>
                )}
              </Button>
              
              {(isAdmin || isHR || isTeamLead) && (
                <Button
                  variant={activeTab === 'approvals' ? 'primary' : 'outline'}
                  onClick={() => setActiveTab('approvals')}
                  className="flex items-center space-x-2"
                >
                  <CheckCircleIcon className="w-4 h-4" />
                  <span>Approvals</span>
                  {pendingApprovals && pendingApprovals.length > 0 && (
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      activeTab === 'approvals' ? 'bg-white text-indigo-600' : 'bg-gray-100 text-gray-600'
                    }`}>
                      {pendingApprovals.length}
                    </span>
                  )}
                </Button>
              )}
            </div>
            
            <Button
              onClick={() => setShowRequestForm((prev) => !prev)}
              icon={PlusIcon}
            >
              {showRequestForm ? 'Close Form' : 'New Request'}
            </Button>
          </div>
        </Card.Body>
      </Card>

      <AnimatePresence>
        {showRequestForm && (
          <motion.div
            key="rectification-request-form"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="overflow-hidden"
          >
            <div ref={requestFormRef} className="scroll-mt-24">
              <Card className="border border-indigo-100 shadow-sm">
                <Card.Body className="p-0">
                  <div className="border-b border-indigo-100 bg-gradient-to-r from-indigo-50 to-purple-50 px-6 py-5">
                    <div className="mb-3 flex items-center gap-2 text-sm text-slate-600">
                      <button
                        type="button"
                        onClick={() => setShowRequestForm(false)}
                        className="font-medium text-slate-700 hover:text-indigo-700"
                      >
                        Attendance Correction
                      </button>
                      <span className="text-slate-300">/</span>
                      <span className="font-medium text-slate-900">New request</span>
                    </div>
                    <h3 className="text-xl font-semibold text-slate-900">Submit Attendance Correction</h3>
                    <p className="mt-1 text-sm text-slate-600">
                      Fill in corrected timings and reason. Multiple-date range is supported up to 10 days.
                    </p>
                  </div>

                  <div className="p-6">
                    <form onSubmit={handleSubmitRequest} className="space-y-6">
                      <div className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Start Date *
                            </label>
                            <input
                              type="date"
                              name="startDate"
                              value={formData.startDate}
                              onChange={handleChange}
                              max={new Date().toISOString().split('T')[0]}
                              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                                errors.startDate ? 'border-red-300' : 'border-gray-300'
                              }`}
                              required
                            />
                            {errors.startDate && (
                              <p className="mt-1 text-sm text-red-600">{errors.startDate[0]}</p>
                            )}
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              End Date *
                            </label>
                            <input
                              type="date"
                              name="endDate"
                              value={formData.endDate}
                              onChange={handleChange}
                              min={formData.startDate || undefined}
                              max={new Date().toISOString().split('T')[0]}
                              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                                errors.endDate ? 'border-red-300' : 'border-gray-300'
                              }`}
                              required
                            />
                            {errors.endDate && (
                              <p className="mt-1 text-sm text-red-600">{errors.endDate[0]}</p>
                            )}
                            {formData.startDate && formData.endDate && (
                              <p className="mt-1 text-xs text-gray-500">
                                Selected range:{' '}
                                {Math.floor((new Date(formData.endDate) - new Date(formData.startDate)) / (1000 * 60 * 60 * 24)) + 1}{' '}
                                day(s), max 10 days
                              </p>
                            )}
                          </div>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Break Time (minutes)
                            </label>
                            <input
                              type="number"
                              name="breaktime"
                              value={formData.breaktime}
                              onChange={handleChange}
                              min="0"
                              max="480"
                              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none ${
                                errors.breaktime ? 'border-red-300' : 'border-gray-300'
                              }`}
                              placeholder="60"
                            />
                            {errors.breaktime && (
                              <p className="mt-1 text-sm text-red-600">{errors.breaktime[0]}</p>
                            )}
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              In Time * <span className="text-gray-400 font-normal">(12-hour)</span>
                            </label>
                            <TimeInput12h
                              value={formData.intime}
                              onChange={(v) => handleChange({ target: { name: 'intime', value: v } })}
                              className={errors.intime ? 'rounded-lg ring-2 ring-red-300' : ''}
                            />
                            {errors.intime && (
                              <p className="mt-1 text-sm text-red-600">{errors.intime[0]}</p>
                            )}
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Out Time <span className="text-gray-400 font-normal">(12-hour)</span>
                            </label>
                            <TimeInput12h
                              value={formData.outtime}
                              onChange={(v) => handleChange({ target: { name: 'outtime', value: v } })}
                              className={errors.outtime ? 'rounded-lg ring-2 ring-red-300' : ''}
                            />
                            {errors.outtime && (
                              <p className="mt-1 text-sm text-red-600">{errors.outtime[0]}</p>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Work Hours Preview */}
                      {formData.intime && formData.outtime && (
                        <div className="p-4 bg-blue-50 rounded-lg">
                          <p className="text-sm text-blue-800">
                            <strong>Calculated Work Hours:</strong> {TimeValidation.calculateWorkHours(
                              formData.intime, 
                              formData.outtime, 
                              `${Math.floor((formData.breaktime || 30) / 60)}:${(formData.breaktime || 30) % 60}`
                            )}
                          </p>
                        </div>
                      )}
                      
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Reason for Correction *
                        </label>
                        <textarea
                          name="reason"
                          value={formData.reason}
                          onChange={handleChange}
                          rows={4}
                          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 ${
                            errors.reason ? 'border-red-300' : 'border-gray-300'
                          }`}
                          placeholder="Please explain why this correction is needed (e.g., forgot to mark attendance, system error, etc.)"
                          required
                        />
                        {errors.reason && (
                          <p className="mt-1 text-sm text-red-600">{errors.reason[0]}</p>
                        )}
                      </div>
                      
                      <div className="bg-blue-50 p-4 rounded-lg">
                        <div className="flex">
                          <ExclamationTriangleIcon className="h-5 w-5 text-blue-400 mt-0.5" />
                          <div className="ml-3">
                            <h4 className="text-sm font-medium text-blue-800">Important Note</h4>
                            <p className="text-sm text-blue-700 mt-1">
                              Please ensure all details are accurate. False information may result in disciplinary action.
                            </p>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => {
                            setShowRequestForm(false);
                            resetForm();
                          }}
                        >
                          Cancel
                        </Button>
                        <Button 
                          type="submit" 
                          icon={DocumentTextIcon}
                          loading={submitting}
                          disabled={!isValid || submitting}
                        >
                          Submit Request
                        </Button>
                      </div>
                    </form>
                  </div>
                </Card.Body>
              </Card>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Filters */}
      <Card>
        <Card.Body className="p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-2">
              <FunnelIcon className="w-4 h-4 text-gray-400" />
              <span className="text-sm font-medium text-gray-900">Filters</span>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={clearFilters}
              className="text-xs px-2 py-1"
            >
              Clear
            </Button>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {/* Status Filter */}
            <div>
              <Dropdown
                value={statusFilter}
                onChange={setStatusFilter}
                options={statusOptions}
                placeholder="Status"
                size="sm"
                className="w-full"
              />
            </div>

            {/* Start Date */}
            <div>
              <input
                type="date"
                value={dateRange.startDate}
                onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="From Date"
              />
            </div>

            {/* End Date */}
            <div>
              <input
                type="date"
                value={dateRange.endDate}
                onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
                className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="To Date"
              />
            </div>
          </div>
        </Card.Body>
      </Card>

      {/* Attendance Requests */}
      <div className="space-y-4">
        {activeTab === 'requests' ? (
          myRequestsLoading ? (
            <LoadingSkeleton />
          ) : (() => {
            const filteredRequests = filterRequests(myRequests);
            return filteredRequests.length > 0 ? (
              <>
                {/* Results count */}
                <div className="flex items-center justify-between">
                  <p className="text-sm text-gray-600">
                    Showing {filteredRequests.length} of {myRequests.length} requests
                  </p>
                </div>
                {filteredRequests.map((request, index) => (
                  <RequestCard key={request.id} request={request} index={index} />
                ))}
                {myRequestsHasMore && (
                  <div className="flex justify-center pt-2">
                    <Button
                      variant="outline"
                      onClick={handleLoadMoreRequests}
                      loading={myRequestsLoadingMore}
                      disabled={myRequestsLoadingMore}
                    >
                      Load More
                    </Button>
                  </div>
                )}
              </>
            ) : myRequests.length > 0 ? (
              <EmptyState 
                icon={FunnelIcon}
                title="No requests match your filters"
                description="Try adjusting your search criteria or clear all filters."
                action={
                  <Button onClick={clearFilters} variant="outline">
                    Clear Filters
                  </Button>
                }
              />
            ) : (
              <EmptyState 
                icon={ClockIcon}
                title="No requests found"
                description="You haven't submitted any attendance correction requests yet."
                action={
                  <Button onClick={() => setShowRequestForm(true)} icon={PlusIcon}>
                    Create Your First Request
                  </Button>
                }
              />
            );
          })()
        ) : (
          (() => {
            const filteredPending = filterRequests(
              pendingApprovals.filter((r) => r.request === 'pending' || !r.request)
            );
            const filteredApproved = filterRequests(
              approvedApprovals.filter((r) => r.request === 'accepted')
            );
            const inboxLoading = approvalsLoading || approvedLoading;
            const hasFilterMismatch =
              (statusFilter === 'rejected' && filteredPending.length === 0 && filteredApproved.length === 0) ||
              (statusFilter === 'pending' && !showPendingApprovalsSection) ||
              (statusFilter === 'accepted' && !showApprovedApprovalsSection);

            if (inboxLoading && pendingApprovals.length === 0 && approvedApprovals.length === 0) {
              return <LoadingSkeleton />;
            }

            const renderPendingSection = () => {
              if (!showPendingApprovalsSection) return null;

              if (approvalsLoading && pendingApprovals.length === 0) {
                return <LoadingSkeleton />;
              }

              if (filteredPending.length > 0) {
                return (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h2 className="text-lg font-semibold text-gray-900">Pending review</h2>
                      <p className="text-sm text-gray-600">
                        {filteredPending.length} of {pendingApprovals.length} pending
                      </p>
                    </div>
                    {filteredPending.map((request, index) => (
                      <ApprovalCard
                        key={request.id}
                        request={request}
                        index={index}
                        onApprove={handleApproveRequest}
                        onReject={(req) => {
                          setSelectedRequest(req);
                          setShowRejectModal(true);
                        }}
                        approving={approving}
                      />
                    ))}
                    {approvalsHasMore && (
                      <div className="flex justify-center pt-2">
                        <Button
                          variant="outline"
                          onClick={handleLoadMoreApprovals}
                          loading={approvalsLoadingMore}
                          disabled={approvalsLoadingMore}
                        >
                          Load More Pending
                        </Button>
                      </div>
                    )}
                  </div>
                );
              }

              if (pendingApprovals.length > 0) {
                return (
                  <EmptyState
                    icon={FunnelIcon}
                    title="No pending requests match your filters"
                    description="Try adjusting your search criteria or clear all filters."
                    action={
                      <Button onClick={clearFilters} variant="outline">
                        Clear Filters
                      </Button>
                    }
                  />
                );
              }

              return (
                <EmptyState
                  icon={ClockIcon}
                  title="No pending requests"
                  description="There are no attendance correction requests awaiting your action."
                />
              );
            };

            const renderApprovedSection = () => {
              if (!showApprovedApprovalsSection) return null;

              if (approvedLoading && approvedApprovals.length === 0) {
                return <LoadingSkeleton />;
              }

              if (filteredApproved.length > 0) {
                return (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h2 className="text-lg font-semibold text-gray-900">Approved</h2>
                      <p className="text-sm text-gray-600">
                        {filteredApproved.length} of {approvedApprovals.length} approved
                      </p>
                    </div>
                    {filteredApproved.map((request, index) => (
                      <ApprovedApprovalCard key={request.id} request={request} index={index} />
                    ))}
                    {approvedHasMore && (
                      <div className="flex justify-center pt-2">
                        <Button
                          variant="outline"
                          onClick={handleLoadMoreApproved}
                          loading={approvedLoadingMore}
                          disabled={approvedLoadingMore}
                        >
                          Load More Approved
                        </Button>
                      </div>
                    )}
                  </div>
                );
              }

              if (approvedApprovals.length > 0) {
                return (
                  <EmptyState
                    icon={FunnelIcon}
                    title="No approved requests match your filters"
                    description="Try adjusting your search criteria or clear all filters."
                    action={
                      <Button onClick={clearFilters} variant="outline">
                        Clear Filters
                      </Button>
                    }
                  />
                );
              }

              return null;
            };

            if (hasFilterMismatch && statusFilter === 'rejected') {
              return (
                <EmptyState
                  icon={XCircleIcon}
                  title="No rejected requests in approvals"
                  description="Rejected requests appear in each employee's My Requests tab."
                />
              );
            }

            const pendingSection = renderPendingSection();
            const approvedSection = renderApprovedSection();

            if (!pendingSection && !approvedSection) {
              return (
                <EmptyState
                  icon={CheckCircleIcon}
                  title="No requests found"
                  description="There are no attendance correction requests in your approvals inbox."
                />
              );
            }

            return (
              <div className="space-y-8">
                {pendingSection}
                {approvedSection}
              </div>
            );
          })()
        )}
      </div>

      {/* Rejection Modal */}
      <Modal
        isOpen={showRejectModal}
        onClose={() => {
          setShowRejectModal(false);
          setRejectionReason('');
          setSelectedRequest(null);
        }}
        title="Reject Attendance Request"
        size="md"
      >
        <div className="p-6">
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Reject Request</h3>
            <p className="text-sm text-gray-600">
              Please provide a clear reason for rejecting this attendance correction request.
            </p>
          </div>

          {selectedRequest && (
            <div className="bg-gray-50 p-4 rounded-lg mb-6">
              <h4 className="text-sm font-medium text-gray-900 mb-2">Request Details</h4>
              <div className="text-sm text-gray-600 space-y-1">
                <p><span className="font-medium">Employee:</span> {selectedRequest.name}</p>
                <p><span className="font-medium">Date:</span> {formatDate(selectedRequest.datestring)}</p>
                <p><span className="font-medium">Time:</span> {formatTime12hFromDb(selectedRequest.intime)} - {formatTime12hFromDb(selectedRequest.outtime)}</p>
              </div>
            </div>
          )}
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Rejection Reason *
              </label>
              <textarea
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                placeholder="Please provide a detailed reason for rejection (e.g., insufficient documentation, policy violation, etc.)"
                required
              />
            </div>
            
            <div className="bg-red-50 p-4 rounded-lg">
              <div className="flex">
                <XCircleIcon className="h-5 w-5 text-red-400 mt-0.5" />
                <div className="ml-3">
                  <h4 className="text-sm font-medium text-red-800">Rejection Notice</h4>
                  <p className="text-sm text-red-700 mt-1">
                    The employee will be notified via email about this rejection and the reason provided.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectionReason('');
                  setSelectedRequest(null);
                }}
              >
                Cancel
              </Button>
              <Button
                onClick={handleRejectRequest}
                variant="danger"
                icon={XCircleIcon}
              >
                Reject Request
              </Button>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default AttendanceCorrection;
